import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook to handle smooth scrolling in the quiz
 * @param {Array} selectedAnswers - Array of answers, where undefined means unanswered
 * @param {Number} totalQuestions - Total number of questions
 * @param {Boolean} isScrolling - Optional flag to prevent unwanted scrolling
 * @param {Number} lastAnsweredIndex - Index of the question that was just answered
 */
const useScrollToNextUnanswered = (
  selectedAnswers, 
  totalQuestions, 
  isScrolling = false,
  lastAnsweredIndex = null
) => {
  // Create a ref to track if we've scrolled to an unanswered question
  const hasScrolledToUnanswered = useRef(false);
  // Create a ref to track the current scroll timeout
  const scrollTimeoutRef = useRef(null);
  // Create a ref to track the last scroll action time
  const lastScrollTimeRef = useRef(0);

  // Function to calculate the optimal offset based on header and progress bar
  const getHeaderOffset = useCallback(() => {
    const progressBarHeight = document.querySelector('.progress-bar-container')?.offsetHeight || 0;
    const headerHeight = document.querySelector('.header')?.offsetHeight || 0;
    return progressBarHeight + headerHeight + 24; // Additional padding
  }, []);

  // Function to scroll to a specific question element with smooth animation
  const scrollToQuestion = useCallback((questionElement, highlightElement = true) => {
    if (!questionElement) return;
    
    const headerOffset = getHeaderOffset();
    const elementPosition = questionElement.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    
    // Use smooth scroll behavior
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });

    // Highlight the question briefly to draw attention - with null check
    if (highlightElement && questionElement) {
      questionElement.classList.add('highlight-active');
      setTimeout(() => {
        // Check if element still exists before removing class
        if (questionElement) {
          questionElement.classList.remove('highlight-active');
        }
      }, 1500);
    }
  }, [getHeaderOffset]);

  // Effect to scroll to next unanswered question when the page loads
  useEffect(() => {
    // Don't scroll if isScrolling flag is true or if we've already scrolled
    if (isScrolling || hasScrolledToUnanswered.current) {
      return;
    }

    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Find the next unanswered question
    const nextUnansweredIndex = selectedAnswers.findIndex(answer => answer === undefined);
    
    // If all questions are answered, don't scroll
    if (nextUnansweredIndex === -1) {
      return;
    }

    // Delayed scroll for better UX
    scrollTimeoutRef.current = setTimeout(() => {
      const questionElement = document.getElementById(`question-${nextUnansweredIndex + 1}`);
      if (questionElement) {
        scrollToQuestion(questionElement);
        hasScrolledToUnanswered.current = true;
      }
    }, 300);

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [selectedAnswers, totalQuestions, isScrolling, scrollToQuestion]);

  // Effect to scroll to the next question when answering a question
  useEffect(() => {
    // Skip if no question was just answered or if we're preventing scrolling
    if (lastAnsweredIndex === null || isScrolling) return;
    
    // Don't scroll if this action happened very recently (debounce)
    const now = Date.now();
    if (now - lastScrollTimeRef.current < 500) return;
    lastScrollTimeRef.current = now;
    
    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Find the next unanswered question after the one just answered
    let nextQuestionIndex = -1;
    
    for (let i = lastAnsweredIndex + 1; i < selectedAnswers.length; i++) {
      if (selectedAnswers[i] === undefined) {
        nextQuestionIndex = i;
        break;
      }
    }
    
    // If there's no next unanswered question, stay on the current one
    if (nextQuestionIndex === -1) {
      return;
    }
    
    // Add a short delay before scrolling to the next question
    scrollTimeoutRef.current = setTimeout(() => {
      const nextQuestion = document.getElementById(`question-${nextQuestionIndex + 1}`);
      if (nextQuestion) {
        scrollToQuestion(nextQuestion);
      }
    }, 500); // Short delay to allow animation of the current selection to complete
    
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [lastAnsweredIndex, selectedAnswers, isScrolling, scrollToQuestion]);

  // Method to reset the scroll status
  const resetScrollStatus = () => {
    hasScrolledToUnanswered.current = false;
  };
  
  // Method to manually scroll to a specific question by index
  const scrollToQuestionByIndex = useCallback((questionIndex) => {
    const questionElement = document.getElementById(`question-${questionIndex + 1}`);
    if (questionElement) {
      scrollToQuestion(questionElement);
    }
  }, [scrollToQuestion]);

  return { resetScrollStatus, scrollToQuestionByIndex };
};

export default useScrollToNextUnanswered;
